import { formatMoney } from "@/utils/format-money";
import {
  type MissionsCampaign,
  currentMission,
  formatProgressValue,
  isCountableProgress,
  missionRewardPrize,
  parseProgressValue,
  progressRatio,
} from "./types";

/** The task kinds that count an amount: their value carries the currency
 *  sign (Sebastian, 2026-10-15: "we want the currency sign if a currency
 *  task, for the progress value"). */
const MONEY_KINDS = new Set(["turnover", "deposit"]);

/** The launcher's view of a missions campaign: the current step's active
 *  task (in order: the one task on show). What the content draws AND what
 *  the control's name says (the module's `rows`), from this one place, so
 *  the two can't drift (👺 Belmont, B4). Flat, for a shallow compare; null
 *  once every step is done. */
/** The task the button shows of a step: of the active, unfinished ones, the
 *  one in focus (`focusId`: the launcher's rotation's turn), else the first
 *  in order (2026-10-22: "start from the first and then we rotate between
 *  them on a 30s interval"). In order only one task is active, so it is
 *  that. */
export function taskOnShow(
  mission: MissionsCampaign["missions"][number],
  focusId: string | undefined
) {
  const open = mission.tasks.filter((t) => t.isActive && !t.isCompleted);
  const pool = open.length ? open : mission.tasks.filter((t) => !t.isCompleted);
  if (pool.length === 0) return undefined;
  const focused = focusId && pool.find((t) => t.id === focusId);
  if (focused) return focused;
  return pool.reduce((first, t) => (t.order < first.order ? t : first));
}

export function missionView(
  mc: MissionsCampaign,
  fallbackCurrency: string | null | undefined,
  focusId?: string
) {
  const mission = currentMission(mc);
  if (!mission) return null;
  const task = taskOnShow(mission, focusId);
  const prize = missionRewardPrize(mission);
  const progress = task?.progress ?? null;
  // An amount in the player's currency (the campaign's, as its minimum
  // bet; else the widget's configured one): the shared money format —
  // the symbol leading, cents only when there are any.
  const currency = mc.minimum_bet.currency ?? fallbackCurrency;
  const money = !!task && MONEY_KINDS.has(task.kind) && !!currency;
  const shown = (raw: string) => {
    const amount = parseProgressValue(raw);
    return money && currency && amount !== null
      ? formatMoney(
          { value: amount, currency },
          { minorUnits: !Number.isInteger(amount) }
        )
      : formatProgressValue(raw);
  };
  const countable = !!progress && isCountableProgress(progress);
  // The step's task map (Sebastian, 2026-10-19: "do you get the prize, or is
  // it just one of many tasks?"): one letter per task in order — d done,
  // c the task on show, o open, l locked (in order: the tasks after the one
  // on show) — flat, so the shallow compare holds.
  const ordered = [...mission.tasks].sort((a, b) => a.order - b.order);
  const at = task ? ordered.indexOf(task) : -1;
  const pips = ordered
    .map((t, i) =>
      t.isCompleted
        ? "d"
        : t === task
          ? "c"
          : mission.keepOrder && at >= 0 && i > at
            ? "l"
            : "o"
    )
    .join("");
  return {
    step: mission.position,
    /** In order: the map is a path, the later tasks locked. */
    inOrder: mission.keepOrder,
    pips,
    /** The tasks' ids in the pips' order (a task's done state by id). */
    taskIds: ordered.map((t) => t.id).join(","),
    /** The step's task lines in the pips' order: the cell is sized once per
     *  step by the longest of them (👺 Belmont, B3). */
    taskLines: ordered.map((t) => t.title).join("\n"),
    /** The task on show (a new one is a new bar). */
    taskKey: task ? `${mission.id}:${task.id}` : null,
    task: task?.title,
    kind: task?.kind,
    value: countable ? shown(progress.current) : null,
    total: countable ? shown(progress.total) : null,
    ratio: progressRatio(progress) ?? 0,
    prize: prize?.title,
    catalog: prize?.is_catalog_item,
  };
}
export type MissionView = NonNullable<ReturnType<typeof missionView>>;
